import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'
Vue.use(Vuex)

import activePropertySlides from './getters/activePropertySlides'

const isProd = process.env.VUE_APP_ENV === 'production'

// Data Paths
const configData = isProd ? '/data/config.json' : process.env.VUE_APP_API_HOST + '/config/touchscreen'
const navigationData = isProd ? '/data/navigation.json' : process.env.VUE_APP_API_HOST + '/navigation/touchscreen'
const pagesData = isProd ? '/data/pages.json' : process.env.VUE_APP_API_HOST + '/pages/touchscreen'
const livingOptionsData = isProd ? '/data/living-options.json' : process.env.VUE_APP_API_HOST + '/living-options/touchscreen'
const subsectionsData = isProd ? '/data/subsections.json' : process.env.VUE_APP_API_HOST + '/subsections/touchscreen'
const propertiesData = isProd ? '/data/properties.json' : process.env.VUE_APP_API_HOST + '/properties/touchscreen?showNotReleased=true'
const dwellingsData = isProd ? '/data/dwellings.json' : process.env.VUE_APP_API_HOST + '/dwellings/touchscreen'
const packagesData = isProd ? '/data/packages.json' : process.env.VUE_APP_API_HOST + '/packages/touchscreen'

// Store Modules
import property from './property.module'
import animations from './animations.module'
import sounds from './sounds.module'
import sidebar from './sidebar.module'
import favourites from './favourites.module'
import interactions from './interactions.module'
import actions from './actions.module'
import filter from './filter.module'
import {
  buildProductMasterplanPath,
  buildLivingOptionPath,
  buildSubsectionPath,
  doProductTypesMatch,
  getCanonicalProductType,
  getDwellingTypeFromDwelling,
  getPropertyTypeFromProperty,
  isLivingEntryRoute,
  isLivingOptionRoute,
  isPropertyRoute,
  isRouteActiveForPath,
  isSubsectionOrPropertyRoute,
} from '@/utils/livingRoute'
import { faLoveseat } from '@fortawesome/pro-light-svg-icons'

const isAvailableProductCarouselProperty = (property) => {
  return !property.isDisplayHome && (property.status || '').toString().toLowerCase() === 'available'
}

const getProductCarouselProperties = (getters, productType = '') => {
  let properties = getters['property/properties'].filter(property => {
    return isAvailableProductCarouselProperty(property)
  })

  const canonicalProductType = getCanonicalProductType(productType)

  if (!canonicalProductType) return properties
  if (canonicalProductType === 'turnkeys') return properties.filter(property => !!property.dwelling)

  return properties.filter(property => doProductTypesMatch(getPropertyTypeFromProperty(property), canonicalProductType))
}

const getProductDwellings = (getters, dwellingType = '') => {
  let dwellings = getters['property/dwellings']

  const canonicalDwellingType = getCanonicalProductType(dwellingType)
  if (!canonicalDwellingType) return dwellings

  return dwellings.filter(dwelling => doProductTypesMatch(getDwellingTypeFromDwelling(dwelling), canonicalDwellingType))
}

const getProductPackages = (getters, packageType = '') => {
  let packages = getters['property/livingOptionsPackages'].filter(_package => {
    return !_package.filter && _package.property && _package.dwelling
  })

  const canonicalPackageType = getCanonicalProductType(packageType)
  if (!canonicalPackageType) return packages

  return packages.filter(_package => doProductTypesMatch(getDwellingTypeFromDwelling(_package.dwelling), canonicalPackageType))
}

const shouldShowSubBottomBarItem = (item, getters) => {
  if (item.customRoute && item.customRoute.includes('/live/packages') && getters['property/packages'].length === 0) return false
  if (item.customRoute && item.customRoute.includes('/product/packages')) return getProductPackages(getters).length > 0
  if (item.customRoute && item.customRoute.includes('/live/turnkeys')) return getProductCarouselProperties(getters, 'turnkeys').length > 0
  if (item.customRoute && item.customRoute.includes('/live/dwellings')) {
    const dwellingTypeMatch = item.customRoute.match(/[?&]dwellingType=([^&#]+)/)
    const dwellingTypeSegment = dwellingTypeMatch ? decodeURIComponent(dwellingTypeMatch[1]) : ''
    return getProductDwellings(getters, dwellingTypeSegment).length > 0
  }

  const liveCarouselMatch = item.customRoute && item.customRoute.match(/\/live\/carousel(?:\/([^/?]+))?/)
  if (liveCarouselMatch) {
    const productTypeSegment = (liveCarouselMatch[1] || '').toString().toLowerCase()
    return getProductCarouselProperties(getters, productTypeSegment).length > 0
  }

  const productDwellingsMatch = item.customRoute && item.customRoute.match(/\/product\/carousel\/dwellings(?:\/([^/?]+))?/)
  if (productDwellingsMatch) {
    const dwellingTypeSegment = (productDwellingsMatch[1] || '').toString().toLowerCase()
    return getProductDwellings(getters, dwellingTypeSegment).length > 0
  }

  const productDwellingMatch = item.customRoute && item.customRoute.match(/\/product\/carousel\/dwelling(?:\/([^/?]+))?/)
  if (productDwellingMatch) {
    const dwellingTypeSegment = (productDwellingMatch[1] || '').toString().toLowerCase()
    return getProductDwellings(getters, dwellingTypeSegment).length > 0
  }

  const productTurnkeysMatch = item.customRoute && item.customRoute.match(/\/product\/carousel\/turnkeys(?:\/([^/?]+))?/)
  if (productTurnkeysMatch) {
    const productTypeSegment = (productTurnkeysMatch[1] || '').toString().toLowerCase()
    return getProductCarouselProperties(getters, productTypeSegment || 'turnkeys').length > 0
  }

  const productPackagesMatch = item.customRoute && item.customRoute.match(/\/product\/carousel\/packages(?:\/([^/?]+))?/)
  if (productPackagesMatch) {
    const packageTypeSegment = (productPackagesMatch[1] || '').toString().toLowerCase()
    return getProductPackages(getters, packageTypeSegment).length > 0
  }

  const productCarouselMatch = item.customRoute && item.customRoute.match(/\/product\/carousel(?:\/([^/?]+))?/)
  if (!productCarouselMatch) return true

  const productCarouselSegment = (productCarouselMatch[1] || '').toString().toLowerCase()
  return getProductCarouselProperties(getters, productCarouselSegment).length > 0
}

export default new Vuex.Store({
  modules: {
    actions,
    animations,
    favourites,
    filter,
    interactions,
    property,
    sounds,
    sidebar
  },

  state: {
    assetsPath: isProd ? '/staged_assets/' : process.env.VUE_APP_ASSETS_HOST,
    version: process.env.VUE_APP_VERSION,
    deviceName: null,
    selector: null,

    config: [],
    navigation: [],
    pages: [],

    isBottomBarVisible: false,
    isMatterportAvailable: null,

    videowallHost: null,
    hideLivingOptions: null,
    hideNavItems: null,
    homeUrl: null,
    appScale: 1,

    newConfig: {},
    language: {},

    activePackageSlideId: '',
    activeTagsOverride: null,
    activeBackgroundOverride: '',
    activeTransitionBackground: null,
    accessCodePrompt: null,
    unlockedAccessCodeEntities: {},

    isLayoutSwitched: false,

    urlHistory: [],

    videoswarmHost: null,
    socket: null,
    navSocketHost: null,
    navSlave: false,
    navMaster: false,
    hideNav: false,
  },

  getters: {
    newConfig: state => state.newConfig,

    activeCategory (state) {
      if (state.route.params.categoryId) {
        return `category/${state.route.params.categoryId}`
      }
    },

    activeBottomBar (state, getters, rootState) {
      const routeName = state.route.name

      if (routeName === 'rest') {
        return []
      }

      let activePageNavigationItem = null
      if (state.route.params.categoryId) {
        activePageNavigationItem = _.find(getters.navigation, category => {
          return category._id === state.route.params.categoryId
        })
      } else if (getters.navigation) {
        getters.navigation.forEach(category => {
          if (state.route.path.includes(category.customRoute)) {
            activePageNavigationItem = category
          }
        })
      }
      let navItems = activePageNavigationItem && activePageNavigationItem.children ? activePageNavigationItem.children : []
      if (rootState.hideNavItems) navItems = navItems.filter(item => !rootState.hideNavItems.split(',').includes(item._id))
      return navItems
    },

    activeBottomBarPageLabel () {
      // dodgey af but cityscape things
      const activeLink = document.querySelector('.bottom-bar .router-link-active')
      if (!activeLink || !activeLink.innerText) return
      return activeLink.innerText.toLowerCase().replaceAll(' ', '-')
    },

    activeSubBottomBar (state, getters, rootState) {
      const routeName = state.route.name

      if (routeName === 'page') {
        let activePageSubNavigation = []
        if (getters.activeBottomBar && getters.activePage) {
          const activePageId = getters.activePage._id
          
          getters.activeBottomBar.forEach(parentNavigationItem => {
            if (parentNavigationItem.pageId === activePageId) {
              activePageSubNavigation = parentNavigationItem.children
            }
            
            if (parentNavigationItem.children) {
              parentNavigationItem.children.forEach(childNavigationItem => {
                if (childNavigationItem.pageId === activePageId) {
                  activePageSubNavigation = parentNavigationItem.children
                }
              })
            }
          })
        }

        if (rootState.hideNavItems) activePageSubNavigation = activePageSubNavigation.filter(item => !rootState.hideNavItems.split(',').includes(item._id)) // hide nav items via URL param

        return activePageSubNavigation.filter(item => shouldShowSubBottomBarItem(item, getters))
      } 
      
      // live section items
      if (routeName !== 'rest') {
        const liveSubNavItems = state.navigation[0].children.find(navItem => navItem.isLiveSection).children
        if (!liveSubNavItems) return []
        return liveSubNavItems.filter(item => shouldShowSubBottomBarItem(item, getters))
      }

      return []
    },

    activeSidebarSubnavigation (state, getters) {
      const routeName = state.route.name
      const { categoryId, livingOptionId } = state.route.params
      const fadeSoldOut = state.newConfig.general.fadeSoldLivingOptions

      // This is used for instances where a custom route is used for the live section
      // It returns getters.activeSubBottomBar instead of subsections for Madeline
      const liveSection = state.navigation[0].children.find(navItem => navItem.isLiveSection)
      const isActiveLiveSection = liveSection && (
        isRouteActiveForPath(state.route, liveSection.customRoute) ||
        (liveSection.customRoute && liveSection.customRoute.includes('/live') && state.route.path.includes('/product/'))
      )

      if (['page', 'live-display-homes', 'dwellings'].includes(routeName)) {
        return getters.activeSubBottomBar
      }

      if (routeName === 'legacy-live-entry') {
        return []
      }

      if (isLivingEntryRoute(routeName)) {
        return _.sortBy(getters['property/activeLivingEntryOptions'], livingOption => livingOption.label)
        .map(livingOption => {
          const isSoldOut = livingOption.isSoldOut || livingOption.availablePropertiesCount === 0
          return {
            _id: livingOption._id,
            label: htmlToString(livingOption.displayName),
            to: buildLivingOptionPath(state.route, livingOption._id, categoryId),
            filter: (fadeSoldOut && isSoldOut) || livingOption.filtered
          }
        })
      }

      if (isLivingOptionRoute(routeName)) {
        const activeLivingOption = getters['property/activeLivingOption']
        if (!activeLivingOption) return []

        const subsections = getters['property/activeSubsections']
        return subsections.map(subsection => {
          return {
            _id: subsection._id,
            label: htmlToString(subsection.displayName),
            to: buildSubsectionPath(state.route, livingOptionId, subsection._id),
            filter: subsection.filtered
          }
        })
      }

      if (isSubsectionOrPropertyRoute(routeName)) {
        const activeSubsection = getters['property/activeSubsection']
        if (!activeSubsection) return []

        const subsectionById = (subsectionId) => getters['property/subsections'].find(subsection => subsection._id === subsectionId)
        const toSubsectionNavItem = (subsection, parentSubsectionId = null) => {
          const parentQuery = parentSubsectionId ? `?parentSubsectionId=${parentSubsectionId}` : ''
          return {
            _id: subsection._id,
            label: htmlToString(subsection.displayName),
            to: buildSubsectionPath(state.route, livingOptionId, subsection._id, parentQuery),
            filter: subsection.filtered
          }
        }

        // handle nested subsections
        if (activeSubsection.subsectionIds && activeSubsection.subsectionIds.length) {
          return getters['property/activeSubsections'].map(subsection => {
            const parentSubsectionId = subsection.parent && subsection.parent.type === 'subsection' ? subsection.parent.id : null
            return toSubsectionNavItem(subsection, parentSubsectionId)
          })
        } else {
          if (activeSubsection.parent.type === 'livingOption') {
            const activeLivingOption = getters['property/activeLivingOption']
            if (!activeLivingOption) return []

            const navItems = activeLivingOption.subsectionIds
              .map(subsectionById)
              .filter(Boolean)
              .map(subsection => toSubsectionNavItem(subsection))

            // Madeline fix
            if (isActiveLiveSection) {
              return getters.activeSubBottomBar
            }

            return navItems
          } else {
            const parentSubsection = subsectionById(activeSubsection.parent.id)
            if (!parentSubsection || !parentSubsection.subsectionIds) return []

            return parentSubsection.subsectionIds
              .map(subsectionById)
              .filter(Boolean)
              .map(subsection => toSubsectionNavItem(subsection, subsection.parent.id))
          }
        }
      }

      return []
    },

    activePage (state) {
      let activePage = null
      if (state.route.params.pageId) {
        activePage = _.find(state.pages, page => {
          return page._id === state.route.params.pageId
        })
      }
      return activePage
    },

    activeBackground (state, getters) {
      const routeName = state.route.name
      const config = state.config

      if (getters.activeBackgroundOverride) {
        return state.assetsPath + getters.activeBackgroundOverride
      }

      if (getters.activePage && getters.activePage.type === 'iframe') {
        return getters.activePage.frameUrl || ''
      }

      if (getters.activePage) {
        const bgImage = getters.activePage.backgroundImage[state.language.code] || getters.activePage.backgroundImage['en']
        return state.assetsPath + bgImage
      }

      if (getters.activePackageSlide) {
        return state.assetsPath + getters.activePackageSlide.backgroundImage
      }

      if (routeName === 'legacy-live-entry') {
        return state.assetsPath + config.liveHereSelectionBackground
      }

      if (isLivingEntryRoute(routeName)) {
        const liveHereBackground = config.liveHereBackground[state.language.code] || config.liveHereBackground['en']
        return state.assetsPath + liveHereBackground
      }

      if (routeName === 'live-display-homes' || routeName === 'live-display-homes-property') {
        const dhBackground = getters['property/activeSubsection'].backgroundImage[state.language.code] ||getters['property/activeSubsection'].backgroundImage['en']
        return state.assetsPath + dhBackground
      }

      if (isLivingOptionRoute(routeName) && getters['property/activeLivingOption']) {
        const bgImage = getters['property/activeLivingOption'].backgroundImage[state.language.code] || getters['property/activeLivingOption'].backgroundImage['en']
        return state.assetsPath + bgImage
      }

      if (isSubsectionOrPropertyRoute(routeName) && getters['property/activeSubsection']) {
        const bgImage = getters['property/activeSubsection'].backgroundImage[state.language.code] || getters['property/activeSubsection'].backgroundImage['en']
        return state.assetsPath + bgImage
      }

      if (['packages', 'dwellings', 'favourites'].includes(routeName)) {
        if (['package-modal', 'dwelling-modal', 'favourite-modal', 'favourites'].includes(state.route.from.name)) {
          return state.animations.animateBackground
        }
      }

      if (['package-modal', 'dwelling-modal', 'favourite-modal'].includes(routeName)) {
        return state.animations.animateBackground
      }

      return ''
    },

    activeSlides (state, getters) {
      const routeName = state.route.name

      if (state.newConfig.general.useSidebarSubnavigation) return []

      if (getters.activePage && getters.activePage.interactiveSlides) {
        return getters.activePage.interactiveSlides
      }

      if (routeName === 'legacy-live-entry') {
        return []
      }

      if (isLivingEntryRoute(routeName)) {
        if (state.newConfig.hideLivingoptionSlides) return []

        const showSoldSlides = state.newConfig.general.showSoldSlides
        const slides = _.sortBy(getters['property/activeLivingEntryOptionsSlides'].filter(option => {
          return option.showNavigationSlide && (showSoldSlides || option.availablePropertiesCount > 0)
        }), livingOption => {
          const [marker] = livingOption.markers || []
          return marker ? marker.x : Number.MAX_SAFE_INTEGER
        })

        slides.map(livingOption => {
          return {
            _id: livingOption._id,
            livingOptionId: livingOption._id,
            label: livingOption.displayName[state.language.code] || livingOption.displayName['en'] || livingOption.label,
            cardBackgroundImage: livingOption.cardBackgroundImage,
            backgroundImage: livingOption.backgroundImage,
            filtered: livingOption.filtered
          }})

        return slides
      }

      if ((isLivingOptionRoute(routeName) || isSubsectionOrPropertyRoute(routeName)) && getters['property/activeLivingOption']) {
        const showSoldSlides = state.newConfig.general.showSoldSlides
        const subsections = getters['property/activeSubsections'].filter(option => {
              return option.showNavigationSlide && (showSoldSlides || option.availablePropertiesCount > 0)
            })

        if (subsections && subsections.length >= 1) {
          return _.sortBy(subsections, subsection => {
            const [marker] = subsection.markers || []
            return marker ? marker.x : Number.MAX_SAFE_INTEGER
          })
        } else {
          return []
        }
      }

      return []
    },

    activeBackgroundOverride (state) {
      return state.activeBackgroundOverride
    },

    activeTagsOverride (state) {
      return state.activeTagsOverride
    },

    activePageTags (state, getters, rootState, rootGetters) {
      let activePageTags = []

      if (getters.activeTagsOverride !== null) {
        return getters.activeTagsOverride
      }

      if (
        isLivingEntryRoute(state.route.name) &&
        rootGetters['config'].liveHereMasterplanTags &&
        rootGetters['config'].liveHereMasterplanTags.length
      ) {
        return rootGetters['config'].liveHereMasterplanTags
      }

      if (
        isLivingOptionRoute(state.route.name) &&
        rootGetters['property/activeLivingOption'] &&
        rootGetters['property/activeLivingOption'].tags
      ) {
        return rootGetters['property/activeLivingOption'].tags
      }

      if (
        isSubsectionOrPropertyRoute(state.route.name) &&
        rootGetters['property/activeSubsection'] &&
        rootGetters['property/activeSubsection'].tags
      ) {
        return rootGetters['property/activeSubsection'].tags
      }

      if (getters.activePage && getters.activePage.tags) {
        activePageTags = _.filter(getters.activePage.tags, tag => {
          return tag.type !== 'feature'
        })

        activePageTags = _.sortBy(activePageTags, tag => { 
          return tag.pos.x
        })
      }

      return activePageTags
    },

    activePackageSlide (state, getters) {
      const activePackage = getters.activePropertySlides.find(_package => _package._id === state.activePackageSlideId)

      if (activePackage) {
        return activePackage
      }

      return null
    },

    activePropertySlides,

    activePropertyModal (state, getters, rootState) {
      const routeName = rootState.route.name

      if (isPropertyRoute(routeName) || routeName === 'carousel') {
        const property = getters['property/activeProperty']
        if (!property) return null

        const dwelling = property.dwelling
        const images = property.dwelling ? [...dwelling.facadeImages, ...(dwelling.floorplanImages[state.language.code] || dwelling.floorplanImages.en), ...dwelling.galleryImages, ...property.viewImages] : [...property.viewImages, ...property.galleryImages]
        const landPrice = property.price ? property.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : false 
        // const dwellingPrice = property.dwelling && dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: 'SAR', maximumSignificantDigits: 10 }) : false 
        const iframes = dwelling ? [...property.iframes, ...dwelling.iframes] : property.iframes
        
        const type = property.dwelling ? 'turnkey' : 'land'


        const hasDefaultPropertyLabel = property.priceLabel[state.language.code] === 'Price' || property.priceLabel[state.language.code] === 'سعر'
        
        return {
          _id: property._id,
          description: dwelling ? dwelling.description : '',
          features: dwelling ? dwelling.featureList : '',
          frontage: property.frontage,
          depth: property.depth,
          images: images,
          viewImageStartIndex: property.dwelling ? images.length - property.viewImages.length : 0,
          viewImageCount: property.viewImages.length,
          type: type,
          labelTitle: state.newConfig.labels.propertyLabel[state.language.code] || state.newConfig.labels.propertyLabel['en'],
          label:  property.displayLabel[state.language.code] || property.displayLabel.en || property.label,
          price: landPrice,
          priceLabel: hasDefaultPropertyLabel ?  state.newConfig.labels.propertyPrice[state.language.code] || state.newConfig.labels.propertyPrice['en'] : property.priceLabel[state.language.code],
          landSize: property.size,
          dwellingSize: dwelling ? dwelling.size : '',
          status: _.kebabCase(property.status),
          dwelling: dwelling,
          suitableDwellings: property.suitableDwellings,
          associatedPackages: property.associatedPackages,
          iframes: iframes,
          viewLabel: property.viewLabel
        }
      }

      if (routeName === 'package-modal') {
        const modalId = rootState.route.params.packageId
        const _package = getters['property/packages'].find(_package => _package._id === modalId)
        if (!_package ) return null

        if (_package && _package.property &&_package.dwelling) {

          const subsection = getters['property/subsections'].find(subsection => subsection._id === _package.property.subsectionId)

          const category = state.navigation[0]._id

          let associatedLotPath = ''

          // if subsection is found
          if (subsection) {
            associatedLotPath = `${buildProductMasterplanPath(category, 'land')}/${_package.property.livingOptionId}/subsection/${_package.property.subsectionId}/property/${_package.property._id}?activeSubsectionId=${_package.property.subsectionId}`
          }

          
          const images = [..._package.dwelling.facadeImages, ...(_package.dwelling.floorplanImages[state.language.code] || _package.dwelling.floorplanImages.en), ..._package.dwelling.galleryImages, ..._package.property.viewImages]

          return {
            _id: _package._id,
            description: _package.dwelling.description,
            dwelling: _package.dwelling,
            features: _package.dwelling.featureList,
            images: images,
            viewImageStartIndex: images.length - _package.property.viewImages.length,
            viewImageCount: _package.property.viewImages.length,
            labelTitle: 'House Name',
            priceLabel: _package.priceLabel && _package.priceLabel[state.language.code] || 'Price',
            label:  _package.displayLabel && _package.displayLabel[state.language.code] || _package.displayLabel.en || _package.label,
            price: _package.price ? _package.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : null,
            size: _package.property.size,
            associatedLotPath: associatedLotPath,
            iframes: [ ..._package.property.iframes, ..._package.dwelling.iframes]
          }
        } else {
          return null
        }
      }

      if (routeName === 'dwelling-modal') {
        const modalId = rootState.route.params.dwellingId
        const dwelling = getters['property/dwellings'].find(dwelling => dwelling._id === modalId)
        if (!dwelling) return null

        if (dwelling) {
          return {
            _id: dwelling._id,
            description: dwelling.description,
            features: dwelling.featureList,
            images: [...dwelling.facadeImages, ...(dwelling.floorplanImages[state.language.code] || dwelling.floorplanImages.en), ...dwelling.galleryImages],
            labelTitle: 'Name',
            label: dwelling.displayName[state.language.code] || dwelling.displayName['en'] || dwelling.label,
            priceLabel: state.newConfig.labels.dwellingPrice[state.language.code] || state.newConfig.labels.dwellingPrice['en'],
            price: dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : '',
            dwellingSize: dwelling.size,
            minimumFrontage: dwelling.minimumFrontageWidth,
            dwelling: dwelling,
            iframes: [ ...dwelling.iframes ],
            viewLabel: {en: ''} // temp fix
          }
        } else {
          return null
        }
      }

      if (routeName === 'live-display-homes-property') {
        const property = getters['property/activeProperty']
        if (!property) return null

        const dwelling = property.dwelling
        if (!dwelling) return null
        
        if (property && dwelling) {
          const fpImages = dwelling.floorplanImages[state.language.code] || dwelling.floorplanImages['en']
          const images = [...dwelling.facadeImages, ...dwelling.galleryImages, ...fpImages, ...property.viewImages]
  
          return {
            type: 'display-home',
            _id: property._id,
            description: dwelling.description,
            features: dwelling.featureList,
            images: images,
            viewImageStartIndex: images.length - property.viewImages.length,
            viewImageCount: property.viewImages.length,
            labelTitle: 'House Design',
            label: dwelling.displayName[state.language.code] || dwelling.displayName['en'] || dwelling.label,
            price: false,
            landSize: property.size,
            dwellingSize: dwelling.size,
            status: 'available',
            dwelling: dwelling,
            isDisplayHome: property.isDisplayHome,
            displayHomeHours: property.displayHomeHours,
            builderDescription: dwelling.builderDescription,
            builderImage: dwelling.builderImage,
            builderLabel: dwelling.builderLabel,
            iframes: [...property.iframes, ...dwelling.iframes]
  
            // associatedPackages: property.associatedPackages
          }
        }
      }


      if (routeName === 'favourite-modal') {
        const modalId = rootState.route.params.favouriteId
        const _package = getters['property/livingOptionsPackages'].find(_package => _package._id === modalId)
        const land = getters['property/land'].find(land => land._id === modalId)
        const dwelling = getters['property/dwellings'].find(dwelling => dwelling._id === modalId)
        const displayHome = getters['property/displayHomes'].find(property => property._id === modalId)

        
        // if (!_package) return null
        if (_package) {
          const hasDefaulPackagePriceLabel = _package.priceLabel[state.language.code] === 'Price' || property.priceLabel[state.language.code] === 'سعر'

          const images = [...(_package.dwelling.floorplanImages[state.language.code] || _.package.dwelling.floorplanImages.en), ..._package.dwelling.galleryImages, ..._package.property.viewImages]

          return {
            _id: _package._id,
            description: _package.dwelling.description,
            features: _package.dwelling.featureList,
            images: images,
            viewImageStartIndex: images.length - _package.property.viewImages.length,
            viewImageCount: _package.property.viewImages.length,
            labelTitle: 'House Name',
            label: _package.label,
            priceLabel: hasDefaulPackagePriceLabel ? state.newConfig.labels.packagePrice[state.language.code] || state.newConfig.labels.packagePrice['en'] : _package.priceLabel[state.language.code],
            price: _package.price ? _package.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : null,
            size: _package.property.size,
            iframes: [ ..._package.property.iframes, ..._package.dwelling.iframes],
            viewLabel: _package.property.viewLabel
          }
        } else if (land) {
          return {
            _id: land._id,
            description: '',
            features: '',
            frontage: land.frontage,
            images: [...land.viewImages, ...land.galleryImages],
            viewImageStartIndex: 0,
            viewImageCount: land.viewImages.length,
            labelTitle: 'Name',
            label: land.displayLabel[state.language.code] || land.displayLabel.en || land.label,
            priceLabel: land.priceLabel[state.language.code] || 'Price',
            price: land.price ? land.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : null,
            landSize: land.size,
            iframes: [ ...land.iframes],
            viewLabel: land.viewLabel
          }
        } else if (dwelling) {
          return {
            _id: dwelling._id,
            description: dwelling.description,
            features: dwelling.featureList,
            images: [...(dwelling.floorplanImages[state.language.code] || dwelling.floorplanImages.en), ...dwelling.galleryImages],
            labelTitle: 'Name',
            label: dwelling.label,
            price: dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : '',
            dwellingSize: dwelling.size,
            iframes: [...dwelling.iframes],
            viewLabel: {en: ''}, // temp fix
            dwelling: dwelling,
            priceLabel: state.newConfig.labels.dwellingPrice[state.language.code] || state.newConfig.labels.dwellingPrice.en
          }
        } else if (getters['property/properties'].some(property => property._id === modalId && property.dwelling)) {
          let property = getters['property/properties'].find(property => property._id === modalId)
          if (!property) return null
          
          const dwelling = property.dwelling
          const images = property.dwelling ? [...dwelling.facadeImages, ...(dwelling.floorplanImages[state.language.code] || dwelling.floorplanImages.en), ...dwelling.galleryImages, ...property.viewImages] : [...property.viewImages, ...property.galleryImages]
          const landPrice = property.price ? property.price.toLocaleString('en-AU', { style: 'currency', currency: state.newConfig.currency.code, maximumSignificantDigits: 10 }) : false 
          // const dwellingPrice = property.dwelling && dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: 'SAR', maximumSignificantDigits: 10 }) : false 
          const iframes = dwelling ? [...property.iframes, ...dwelling.iframes] : property.iframes
          
          const type = property.dwelling ? 'turnkey' : 'land'

          return {
            _id: property._id,
            description: dwelling ? dwelling.description : '',
            features: dwelling ? dwelling.featureList : '',
            frontage: property.frontage,
            images: images,
            viewImageStartIndex: property.dwelling ? images.length - property.viewImages.length : 0,
            viewImageCount: property.viewImages.length,
            type: type,
            labelTitle: state.newConfig.labels.propertyLabel[state.language.code] || state.newConfig.labels.propertyLabel['en'],
            label: property.label,
            price: landPrice,
            priceLabel: state.newConfig.labels.propertyPrice[state.language.code] || state.newConfig.labels.propertyPrice['en'],
            landSize: property.size,
            dwellingSize: dwelling ? dwelling.size : '',
            status: _.kebabCase(property.status),
            dwelling: dwelling,
            suitableDwellings: property.suitableDwellings,
            associatedPackages: property.associatedPackages,
            iframes: iframes
          }
        } else if (displayHome) {
          const images = [...displayHome.dwelling.facadeImages, ...displayHome.dwelling.floorplanImages, ...displayHome.dwelling.galleryImages, ...displayHome.viewImages]

          return {
            _id: displayHome._id,
            description: displayHome.dwelling.description,
            displayHomeHours: displayHome.displayHomeHours,
            features: displayHome.dwelling.featureList,
            images: images,
            viewImageStartIndex: images.length - displayHome.viewImages.length,
            viewImageCount: displayHome.viewImages.length,
            label: displayHome.dwelling.label,
            size: displayHome.dwelling.size,
            iframes: [ ...displayHome.dwelling.iframes], 
            dwelling: displayHome.dwelling,
            isDisplayHome: true,
            type: 'display-home'
          }
        } else {
          return null
        }
      }
    },

    activeBackLink (state) {
      return state.urlHistory[state.urlHistory.length - 1] || '/'
    },

    config (state) {
      return state.config
    },

    navigation (state) {
      let navigation = JSON.parse(JSON.stringify(state.navigation))
      if (navigation && navigation.length) {
        navigation.forEach(navigationCategory => {
          navigationCategory.children = _.sortBy(navigationCategory.children, child => child.order)
        })
      }
      return navigation
    },

    isLivePage (state, getters, rootState) {
      const { livingOptionId } = rootState.route.params

      if (!livingOptionId) return false
      return true
    },

    language (state) {
      return state.language
    },

    languages (state) {
      return state.newConfig && state.newConfig.languages || []
    },

    appScale (state) {
      return state.appScale
    },
  },

  mutations: {
    SET_APP_SCALE (state, scale) {
      state.appScale = scale
    },

    SET_CONFIG (state, config) {
      state.config = config
    },

    SET_MATTERPORT_AVAILABILITY (state, is_available) {
      state.isMatterportAvailable = is_available
    },

    SET_NAVIGATION (state, navigation) {
      state.navigation = navigation
    },

    SET_PAGES (state, pages) {
      state.pages = pages
    },

    SET_LANGUAGE (state, language) {
      state.language = language
    },

    SET_ACTIVE_PACKAGE_SLIDE_ID (state, id) {
      state.activePackageSlideId = id
    },

    SET_ACTIVE_BACKGROUND_OVERRIDE (state, background) {
      state.activeBackgroundOverride = background
    },

    SET_ACTIVE_TAGS_OVERRIDE (state, tags) {
      state.activeTagsOverride = tags
    },

    OPEN_ACCESS_CODE_PROMPT (state, prompt) {
      state.accessCodePrompt = prompt
    },

    CLOSE_ACCESS_CODE_PROMPT (state) {
      state.accessCodePrompt = null
    },

    UNLOCK_ACCESS_CODE_ENTITY (state, entityId) {
      if (entityId) {
        Vue.set(state.unlockedAccessCodeEntities, entityId, Date.now())
      }
    },

    SET_IS_LAYOUT_SWITCHED (state, switchState) {
      state.isLayoutSwitched = switchState
    },

    ADD_URL_TO_HISTORY (state, url) {
      state.urlHistory.push(url)
    },

    SPLICE_HISTORY_FROM_URL (state, url) {
      const urlIndex = state.urlHistory.indexOf(url)
      
      state.urlHistory.splice(urlIndex, state.urlHistory.length)
    },

    CLEAR_URL_HISTORY (state) {
      state.urlHistory = []
    },

    SET_NEW_CONFIG (state, config) {
      state.newConfig = config 
      state.language = config.language
    }
  },

  actions: {
    snitch ({ state }) {
      axios.get(`https://snitch.sensoreal.com.au/touch-seen/${state.deviceName}`).catch(e => {
        console.error(e)
      })
    },

    async fetchMatterportAvailability ({ state, commit }) {
      // check the cached state so we dont make unnecessary requests
      if (state.isMatterportAvailable !== null) {
        return state.isMatterportAvailable
      } else {
        await axios.get('https://my.matterport.com', {
          timeout: 5000,
        }).then(() => {
          commit('SET_MATTERPORT_AVAILABILITY', true)
        }).catch(e => {
          if (e.response && e.response.status == 403) {
            commit('SET_MATTERPORT_AVAILABILITY', true)
          } else {
            commit('SET_MATTERPORT_AVAILABILITY', true)
          }
        }).finally(() => {
          return state.isMatterportAvailable
        })
      }
    },

    async fetchData ({ commit }) {
      await axios.get(configData).then(res => {
        const configItems = res.data.config
        // console.log(configItems)
        let configObject = {}
        configItems.map(el => {
          configObject[el.key] = el.value
        })
        // console.log(configObject)
        commit('SET_CONFIG', configObject)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(navigationData).then(res => {
        commit('SET_NAVIGATION', res.data.categories)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(pagesData).then(res => {
        commit('SET_PAGES', res.data)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(livingOptionsData).then(res => {
        commit('property/SET_LIVING_OPTIONS', res.data)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(subsectionsData).then(res => {
        commit('property/SET_SUBSECTIONS', res.data)
      }).catch(err => {
        console.warn(err)
      })
      
      await axios.get(propertiesData).then(res => {
        commit('property/SET_PROPERTIES', res.data)
      }).catch(err => {
        console.warn(err)
      })
      
      await axios.get(dwellingsData).then(res => {
        commit('property/SET_DWELLINGS', res.data)
      }).catch(err => {
        console.warn(err)
      })
      
      await axios.get(packagesData).then(res => {
        commit('property/SET_PACKAGES', res.data)
      }).catch(err => {
        console.warn(err)
      })

    },

    toggleBottomBarVisibility ({ state }) {
      state.isBottomBarVisible = !state.isBottomBarVisible
    },
    
    announceSwarmContext({ state }, context) {
      if (state.videoswarmHost && context) {
        // console.log(`GET ${state.videowallHost}/play/${videoFilename}`)
        axios.get(`${state.videoswarmHost}/context/${context}`)
      }
    },

    triggerVideo ({ state }, videoFilename) {
      if (state.videowallHost && videoFilename) {
        // console.log(`GET ${state.videowallHost}/play/${videoFilename}`)
        axios.get(`${state.videowallHost}/play/${videoFilename}`)
      }
    }
  }
})

function htmlToString (html) {
  if (typeof html === 'object') {
    let object = html

    for (const [key, value] of Object.entries(html)) {
      const newValue = value.replaceAll(/<[^>]*>/g, " ");
      object[key] = newValue
    }

    return object
  }
  
  if (typeof html === 'string') {
    const newValue = html.replaceAll(/<[^>]*>/g, " ");
    return newValue;
  }

  return "";
}
